import { useEffect } from "react";
import { REVEAL_THRESHOLD, REVEAL_ROOT_MARGIN } from "../config/constants";

/**
 * Attaches an IntersectionObserver to all `.reveal` elements and adds the
 * `revealed` class when they enter the viewport.
 *
 * Also watches for dynamically added `.reveal` elements (e.g. after an async
 * Sanity fetch resolves) via a MutationObserver on document.body.
 *
 * Both observers are disconnected on unmount — no memory leaks.
 */
export function useIntersectionReveal() {
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: REVEAL_THRESHOLD, rootMargin: REVEAL_ROOT_MARGIN }
    );

    // Observe elements already in the DOM
    const observe = (root) =>
      root.querySelectorAll(".reveal:not(.revealed)").forEach((el) => io.observe(el));

    observe(document);

    // Watch for new .reveal elements added after mount (e.g. async data)
    const mo = new MutationObserver((mutations) => {
      mutations.forEach((m) =>
        m.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          if (node.classList?.contains("reveal")) io.observe(node);
          observe(node); // also check descendants
        })
      );
    });

    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      mo.disconnect();
    };
  }, []); // intentionally empty — runs once after mount
}
